<script setup>
import BellIcon from '~/assets/images/wa-icon-bell.svg'
import { getNode } from '@formkit/core'
import { fullURL } from '#common/utils/url'
const route = useRoute()

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  product: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['close', 'saved'])

const { $auth, $axios } = useNuxtApp()
const userListStore = useUserList()

// --- STATE ---
const isLoading = ref(false)
const errors = ref({})

// A single reactive object for all form data
const formData = ref({})

// --- COMPUTED ---
const productStatus = computed(() =>
  userListStore.getProductStatus(props.product.product_id),
)
const allLists = computed(() => userListStore.allLists)

// Generates options for the list checkboxes
const listOptions = computed(() => {
  return allLists.value.map((list) => ({
    label: list.name,
    value: list.id,
  }))
})

// --- METHODS ---
const closeModal = () => {
  if (!isLoading.value) {
    emit('close')
  }
}

const scrollToBottom = () => {
  const modal = document.getElementById('add-wine-form')
  modal.scrollTo(0, modal.scrollHeight)
}

/**
 * Sets the initial state of the form based on the product's status.
 */
const initializeState = () => {
  errors.value = {}
  const initialData = {
    notifyMeSelected: false,
    selectedListIds: [],
    createNewListChecked: false,
    newListName: '',
  }

  if (productStatus.value) {
    initialData.notifyMeSelected = productStatus.value.notify_me
    initialData.selectedListIds = productStatus.value.lists.map(
      (list) => list.id,
    )
  }
  formData.value = initialData

  // Reset validation state if the form already exists
  const formNode = getNode('add-wine-form')
  if (formNode) {
    formNode.reset()
  }
}

// --- HANDLERS ---
const handleEsc = (e) => {
  if (e.key === 'Escape' && props.visible) {
    closeModal()
  }
}
const handleSave = async (data) => {
  isLoading.value = true
  errors.value = {}

  // --- Track changes for the toast notification ---
  const changes = {
    notificationStatus: null,
    listsAdded: 0,
    listsRemoved: 0,
  }
  const originalStatus = userListStore.getProductStatus(
    props.product.product_id,
  )
  const originalNotifyMe = originalStatus?.notify_me ?? false
  const originalListIds = new Set(originalStatus?.lists?.map((l) => l.id) ?? [])

  // 1. Check notification status change
  if (data.notifyMeSelected !== originalNotifyMe) {
    changes.notificationStatus = data.notifyMeSelected ? 'created' : 'canceled'
  }

  const selectedListIdsSet = new Set(data.selectedListIds)

  // 2. Calculate added lists
  for (const listId of selectedListIdsSet) {
    if (!originalListIds.has(listId)) {
      changes.listsAdded++
    }
  }

  // 3. Calculate removed lists
  for (const listId of originalListIds) {
    if (!selectedListIdsSet.has(listId)) {
      changes.listsRemoved++
    }
  }

  const apiCalls = []

  // Toggle notify me if it has changed
  if (data.notifyMeSelected !== originalNotifyMe) {
    apiCalls.push(
      $axios.post('/api/customer/toggle-product-subscription/', {
        product_id: props.product.product_id,
        referrer_url: fullURL(route),
      }),
    )
  }

  // Add to lists that are newly selected
  for (const listId of selectedListIdsSet) {
    if (!originalListIds.has(listId)) {
      apiCalls.push(
        $axios.post('/api/customer/list-products/add/', {
          user_list: listId,
          product: props.product.product_id,
        }),
      )
    }
  }
  // Remove from lists that were unselected
  for (const list of originalStatus?.lists ?? []) {
    if (!selectedListIdsSet.has(list.id)) {
      if (!list.products) {
        await userListStore.loadProducts(list)
      }
      const userListProduct = list.products.find(
        (p) => p.product.id === props.product.product_id,
      )
      if (userListProduct) {
        apiCalls.push(
          $axios.delete(`/api/customer/list-products/${userListProduct.id}/`),
        )
      }
    }
  }
  // Create a new list and add the product to it
  if (data.newListName && data.createNewListChecked) {
    changes.listsAdded++
    try {
      const user = $auth.user
      const creatorName =
        `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
      const newListResponse = await $axios.post('/api/customer/lists/', {
        name: data.newListName,
        creator: creatorName,
      })
      const newListId = newListResponse.data.id
      await $axios.post('/api/customer/list-products/add/', {
        user_list: newListId,
        product: props.product.product_id,
      })
    } catch (error) {
      console.error(
        'Failed to create new list or add product:',
        error,
        error.response?.data,
      )
      isLoading.value = false
      return
    }
  }

  try {
    if (apiCalls.length > 0) {
      await Promise.all(apiCalls)
    }
    await userListStore.loadAllLists(true)
    await userListStore.fetchProductStatusImmediately(props.product.product_id)

    if (changes.notificationStatus) {
      const sub = await apiCalls[0]
      $auth.user.notifyme_products[sub.data.product] = sub.data
    }

    // Emit the 'saved' event with the changes if there were any
    if (
      changes.notificationStatus ||
      changes.listsAdded > 0 ||
      changes.listsRemoved > 0
    ) {
      emit('saved', changes)
    }

    emit('close')
  } catch (error) {
    console.error('Failed to save list changes:', error)
    errors.value.general =
      'An error occurred while saving your changes. Please try again.'
  } finally {
    isLoading.value = false
  }
}

// --- WATCHERS ---
watch(
  () => formData.value.createNewListChecked,
  async (isChecked) => {
    if (isChecked) {
      await nextTick()
      scrollToBottom()
      const node = getNode('new-list-name')
      if (node) {
        node.input.focus()
      }
    }
  },
)

watch(
  () => formData.value.newListName,
  (newVal) => {
    if (newVal && !formData.value.createNewListChecked) {
      formData.value.createNewListChecked = true
    }
  },
)

watch(
  () => props.visible,
  (isVisible) => {
    if (typeof window !== 'undefined') {
      if (isVisible) {
        window.addEventListener('keydown', handleEsc)
        document.body.classList.add('modal-open-no-scroll')
      } else {
        window.removeEventListener('keydown', handleEsc)
        document.body.classList.remove('modal-open-no-scroll')
      }
    }
    if (isVisible) {
      initializeState()
    }
  },
)

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', handleEsc)
    document.body.classList.remove('modal-open-no-scroll')
  }
})
</script>

<template>
  <div class="user-list-modal">
    <div v-if="visible" class="modal-mask" @click.self="closeModal">
      <div class="modal-wrapper">
        <div class="modal-container user-list-modal">
          <div class="modal-header">
            <h1>Add a Wine</h1>
            <button
              class="close-button"
              aria-label="Close modal"
              @click="closeModal"
            >
              &times;
            </button>
          </div>

          <FormKit
            id="add-wine-form"
            v-model="formData"
            type="form"
            :actions="false"
            @submit="handleSave"
          >
            <p v-if="product" class="product-name">
              Add {{ product.title }} to a list
            </p>

            <div class="checkbox-list">
              <div class="form-group">
                <FormKit
                  id="saved-input"
                  type="checkbox"
                  name="notifyMeSelected"
                  :value="true"
                />
                <div class="saved-label-content">
                  <label for="saved-input" class="label-line-1">Saved</label>
                  <div class="label-line-2-wrapper">
                    <label
                      for="saved-input"
                      class="icon-wrapper"
                      aria-label="Toggle Saved notification"
                    >
                      <img :src="BellIcon" alt="" />
                    </label>
                    <label for="saved-input" class="label-line-2"
                      >Notify me when the wines from this producer become
                      available</label
                    >
                  </div>
                </div>
              </div>

              <FormKit
                type="checkbox"
                name="selectedListIds"
                :options="listOptions"
                decorator-class="list-item"
              />

              <div class="form-group create-new-list">
                <div class="create-list-toggle">
                  <FormKit
                    id="create-new-list-checkbox"
                    type="checkbox"
                    name="createNewListChecked"
                    label="Create a new list"
                  />
                </div>

                <div
                  v-show="formData.createNewListChecked"
                  class="new-list-input-wrapper"
                >
                  <div class="input-header">
                    <label for="new-list-name" class="input-label"
                      >List Name*</label
                    >
                    <span class="char-count"
                      >{{ (formData.newListName || '').length }}/36</span
                    >
                  </div>
                  <FormKit
                    id="new-list-name"
                    type="text"
                    name="newListName"
                    maxlength="36"
                    :validation="
                      formData.createNewListChecked
                        ? 'required:trim|length:1,36|no_whitespace_rule'
                        : ''
                    "
                    :validation-messages="{
                      required: 'Error: List Name is required.',
                      length:
                        'Error: List Name must be between 1 and 36 characters.',
                    }"
                  >
                    <template #default="{ slots, classes }">
                      <div :class="classes.wrapper">
                        <div :class="classes.inner">
                          <component :is="slots.input" />
                        </div>
                        <component :is="slots.messages" />
                      </div>
                    </template>
                  </FormKit>
                </div>
              </div>
            </div>
          </FormKit>

          <div class="modal-footer">
            <div v-if="errors.general" class="form-error">
              {{ errors.general }}
            </div>
            <button
              form="add-wine-form"
              type="submit"
              class="button"
              :disabled="isLoading"
            >
              {{ isLoading ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
